import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  goodsTypeLabel,
  normaliseGoodsTypes,
  normalisePackageServices,
  normalisePackageStatuses,
  normalisePackages,
  toAddPackageParams,
  toUpdatePackageParams,
  type Package,
  type PackageDraft,
} from '@mypoz/shared';
import { useLocale } from '@mypoz/i18n';

import { PackagesContext, type PackagesState } from './context.js';
import { ADD_PACKAGE_CLEAN_REFUSALS, DELETE_PACKAGE_CLEAN_REFUSALS } from './dictionary.js';
import {
  addPackage as sendAddPackage,
  deletePackage as sendDeletePackage,
  fetchGoodsTypes,
  fetchPackageRows,
  fetchPackageServices,
  fetchPackageStatusRows,
  PackagesRequestError,
} from './api.js';

/**
 * Every package on the account, fetched once for the whole app (MYP-40).
 *
 * Above `Layout` rather than inside the Packages screen, for the same reason `WarehousesProvider`
 * is: more than one screen wants the same list and they must agree on it — the list itself, and
 * the Dashboard's ready-to-order tile once MYP-27 wires it.
 *
 * Three requests, not one: the list, the vendor's status dictionary that gives each row its
 * label, and its goods-type dictionary (MYP-102) that the filter and the add form offer. They are
 * fetched together because a row without its label renders a bare code, and run concurrently
 * because none depends on another. Goods types fail with the rest rather than degrading on their
 * own: a missing dictionary would leave the form's goods-type choice with nothing to offer, and
 * one retry for the whole screen is simpler to reason about than a partly-loaded one.
 *
 * Warehouse services (MYP-100) are the exception, fetched alongside but settled on their own. They
 * only matter inside the add/edit sheet, so a failed `packages_services` call leaves the list
 * usable and says so in the sheet's services section instead. `reload()` retries everything;
 * `reloadServices()` retries only the services, without blanking the list.
 *
 * The raw rows are what is held, not the normalised list — the same decision `WarehousesProvider`
 * documents. The vendor stores each status label twice, once Chinese and once English, so
 * switching language re-resolves rows already in memory rather than asking for data that did not
 * change, and the language toggle stays instant at an endpoint that is not fast.
 *
 * Nothing is cached beyond the session and there is no polling: intake happens on the warehouse's
 * clock, not the customer's, and a reload is an honest way to ask whether anything moved.
 */
export function PackagesProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const [rows, setRows] = useState<readonly unknown[] | undefined>(undefined);
  const [statusRows, setStatusRows] = useState<readonly unknown[]>([]);
  const [goodsTypeMap, setGoodsTypeMap] = useState<Readonly<Record<string, unknown>>>({});
  const [serviceMap, setServiceMap] = useState<Readonly<Record<string, unknown>> | undefined>(
    undefined,
  );
  const [servicesFailed, setServicesFailed] = useState(false);
  const [servicesAttempt, setServicesAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  /*
    The two dictionaries load on their own (MYP-126): the paged Packages screen needs them to label
    its rows, and should not wait for the whole account's list, which only the Dashboard, Create
    order and notification links still read.
  */
  const [dictionariesReady, setDictionariesReady] = useState(false);

  useEffect(() => {
    // Guards both overlapping-fetch cases at once: StrictMode's dev double-mount, and a reload
    // pressed while the previous attempt is still running. Only the newest effect's result lands.
    let current = true;

    setRows(undefined);
    setFailed(false);
    setDictionariesReady(false);

    const dictionaries = Promise.all([fetchPackageStatusRows(), fetchGoodsTypes()]).then(
      ([nextStatuses, nextGoodsTypes]) => {
        if (!current) return;
        setStatusRows(nextStatuses);
        setGoodsTypeMap(nextGoodsTypes);
        setDictionariesReady(true);
      },
    );

    Promise.all([fetchPackageRows(), dictionaries]).then(
      ([nextRows]) => {
        if (!current) return;
        setRows(nextRows);
      },
      (cause: unknown) => {
        if (!current) return;
        // Logged, not shown. The vendor's failure text is Chinese prose from its own code table:
        // useful to whoever is debugging, not something to put in front of a customer.
        console.error('[packages] could not load the package list', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt]);

  // Services settle on their own clock (see above), so they retry on their own too: retrying them
  // from inside an open Edit sheet must not blank the package list that sheet is showing.
  useEffect(() => {
    let current = true;

    setServiceMap(undefined);
    setServicesFailed(false);

    fetchPackageServices().then(
      (nextServices) => {
        if (current) setServiceMap(nextServices);
      },
      (cause: unknown) => {
        if (!current) return;
        console.error('[packages] could not load warehouse services', cause);
        setServicesFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt, servicesAttempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);
  const reloadServices = useCallback(() => setServicesAttempt((previous) => previous + 1), []);

  /*
    Write-then-refetch (MYP-98): `add_packages` answers with no package in the body, so there is
    nothing to insert optimistically — the list is simply read again. On failure the list is also
    re-read unless the vendor gave a clean refusal, because "failed" is not the same as "nothing
    was written": code 2 and 3 are partial failures, and a timeout may have landed server-side.
  */
  const addPackage = useCallback(
    async (draft: PackageDraft) => {
      try {
        await sendAddPackage(toAddPackageParams(draft));
      } catch (cause) {
        const clean =
          cause instanceof PackagesRequestError && ADD_PACKAGE_CLEAN_REFUSALS.includes(cause.code);
        if (!clean) reload();
        throw cause;
      }
      reload();
    },
    [reload],
  );

  /*
    An edit (MYP-123) is the same request as an add — the vendor updates when the tracking number
    already exists — so it gets the same write-then-refetch and the same "re-read unless refused
    cleanly" rule.
  */
  const updatePackage = useCallback(
    async (draft: PackageDraft, original: Package) => {
      try {
        await sendAddPackage(toUpdatePackageParams(draft, original));
      } catch (cause) {
        const clean =
          cause instanceof PackagesRequestError && ADD_PACKAGE_CLEAN_REFUSALS.includes(cause.code);
        if (!clean) reload();
        throw cause;
      }
      reload();
    },
    [reload],
  );

  // Same write-then-refetch shape as Add (MYP-99): gone from the list only once the vendor's own
  // list no longer has it, never removed locally on the strength of a request alone.
  const deletePackage = useCallback(
    async (packageId: string) => {
      try {
        await sendDeletePackage(packageId);
      } catch (cause) {
        const clean =
          cause instanceof PackagesRequestError &&
          DELETE_PACKAGE_CLEAN_REFUSALS.includes(cause.code);
        if (!clean) reload();
        throw cause;
      }
      reload();
    },
    [reload],
  );

  const value = useMemo<PackagesState>(() => {
    // The vendor has no Malay copy for its statuses, so ms reads the English side — the same
    // compromise `normaliseWarehouses` already makes for warehouse names and notices.
    // Goods types follow the same rule — the vendor publishes English and Chinese only.
    const language = locale === 'zh' ? 'zh' : 'en';
    const statuses = normalisePackageStatuses(statusRows);
    const packages = rows ? normalisePackages(rows, statuses, language) : [];
    const goodsTypeEntries = normaliseGoodsTypes(goodsTypeMap);
    const servicesByWarehouse = serviceMap ? normalisePackageServices(serviceMap, language) : {};

    return {
      packages,
      status: rows ? 'ready' : failed ? 'error' : 'loading',
      dictionariesStatus: dictionariesReady ? 'ready' : failed ? 'error' : 'loading',
      toPackages: (pageRows: readonly unknown[]) => normalisePackages(pageRows, statuses, language),
      listVersion: attempt,
      goodsTypes: goodsTypeEntries.map((entry) => ({
        code: entry.code,
        label: goodsTypeLabel(goodsTypeEntries, entry.code, language),
      })),
      servicesStatus: serviceMap ? 'ready' : servicesFailed ? 'error' : 'loading',
      servicesFor: (warehouseId: string) => servicesByWarehouse[warehouseId] ?? [],
      reload,
      reloadServices,
      addPackage,
      deletePackage,
      updatePackage,
    };
  }, [
    rows,
    dictionariesReady,
    attempt,
    statusRows,
    goodsTypeMap,
    serviceMap,
    servicesFailed,
    failed,
    locale,
    reload,
    reloadServices,
    addPackage,
    deletePackage,
    updatePackage,
  ]);

  return <PackagesContext.Provider value={value}>{children}</PackagesContext.Provider>;
}
